'use client'

import { Accordion as AccordionPrimitive } from '@base-ui/react/accordion'
import { PlusIcon } from 'lucide-react'

import { cn } from '@/shared/lib/cn'

function Accordion({ className, ...props }: AccordionPrimitive.Root.Props) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      // Закрытые панели остаются в разметке под `hidden="until-found"`. Обычное скрытие прячет
      // текст и от поиска по странице тоже, а так браузер находит слово внутри закрытой панели
      // и сам её раскрывает; поисковый робот такой текст тоже видит.
      hiddenUntilFound
      className={cn('flex w-full flex-col gap-3', className)}
      {...props}
    />
  )
}

function AccordionItem({ className, ...props }: AccordionPrimitive.Item.Props) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('bg-card rounded-xl border px-5', className)}
      {...props}
    />
  )
}

function AccordionTrigger({ className, children, ...props }: AccordionPrimitive.Trigger.Props) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'focus-visible:ring-ring/50 flex flex-1 cursor-pointer items-center justify-between gap-4 py-4 text-left [font-size:var(--type-body-font-size)] [font-weight:var(--type-label-font-weight)] [line-height:var(--type-body-line-height)] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        {...props}
      >
        {children}
        {/* Знак «плюс» разворачивается в «крестик»: одна фигура вместо двух разных значков. */}
        <span className="bg-accent text-primary flex size-7 shrink-0 items-center justify-center rounded-full transition-transform duration-200 [[data-panel-open]_&]:rotate-45 motion-reduce:transition-none">
          <PlusIcon aria-hidden="true" className="size-4" />
        </span>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({ className, children, ...props }: AccordionPrimitive.Panel.Props) {
  return (
    <AccordionPrimitive.Panel
      data-slot="accordion-content"
      className="data-[closed]:animate-accordion-collapse data-[open]:animate-accordion-expand text-muted-foreground overflow-hidden [font-size:var(--type-supporting-font-size)] [font-weight:var(--type-supporting-font-weight)] [line-height:var(--type-supporting-line-height)] motion-reduce:!animate-none"
      {...props}
    >
      <div className={cn('pt-0 pb-4', className)}>{children}</div>
    </AccordionPrimitive.Panel>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
